import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Leaf, Settings as Cog, LifeBuoy, UserPen, LogOut, ChevronRight, Package, Trophy, CalendarDays, ClipboardList, Clock, CheckCircle2, XCircle, IndianRupee, MessageSquare, ShieldCheck, Circle, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/Layout";
import { ReferralCard } from "@/components/ReferralCard";
import { InviteLeaderboard } from "@/components/InviteLeaderboard";
import { PointsCard } from "@/components/PointsCard";
import { VerifiedTick } from "@/components/VerifiedTick";

const Stat = ({ Icon, value, label, testid }) => (
  <div className="rounded-2xl bg-[#F4F8F5] p-4">
    <Icon className="mb-2 h-5 w-5 text-[#2E9E6B]" />
    <p className="font-display text-2xl font-extrabold text-[#0D3B2B]" data-testid={testid}>{value}</p>
    <p className="text-xs font-semibold text-[#4A6358]">{label}</p>
  </div>
);

export const useLogout = () => {
  const { setUser } = useApp();
  const nav = useNavigate();
  return async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    toast.success("Logged out");
    nav("/login", { replace: true });
  };
};

export default function Profile() {
  const { user, summary, t, refreshSummary } = useApp();
  useEffect(() => { refreshSummary(); }, [refreshSummary]);
  const c = summary?.counts || {};
  const checks = summary?.checks || [];
  const done = checks.filter((x) => x.done).length;
  const logout = useLogout();
  const since = user.created_at ? new Date(user.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : "–";
  const links = [
    ["/history", ClipboardList, "My pickup history", "profile-history-link"],
    ["/profile/edit", UserPen, "Edit profile", "profile-edit-link"],
    ["/profile/addresses", MapPin, "My addresses", "profile-addresses-link"],
    ["/profile/kyc", ShieldCheck, "KYC verification", "profile-kyc-link"],
    ["/impact", Leaf, "My environmental impact", "profile-impact-link"],
    ["/help", LifeBuoy, "Help and complaints", "profile-help-link"],
    ["/settings", Cog, "Settings", "profile-settings-link"],
  ];
  return (
    <div className="space-y-5">
      <Card className="flex items-center gap-4">
        {user.picture ? <img src={user.picture} alt="" className="h-16 w-16 rounded-full object-cover" />
          : <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#2E9E6B] font-display text-2xl font-bold text-white">{(user.full_name || "K")[0]}</div>}
        <div className="min-w-0">
          <p className="truncate font-display text-xl font-bold text-[#0D3B2B]" data-testid="profile-name">{user.business_name || user.full_name || user.name || user.mobile}</p>
          <p className="text-sm text-[#4A6358]">{t(user.customer_type)}{user.area ? ` · ${user.area}` : ""}</p>
          <p className="text-xs text-[#7B968B]" data-testid="profile-username">{user.username ? `@${user.username} · ` : ""}{user.mobile}{user.email_verified ? ` · ${t("Verified")}` : ""}</p>
          <div className="mt-1"><VerifiedTick ok={user.mobile_verified} label={t("Mobile verified")} testid="profile-mobile-verified" /></div>
        </div>
      </Card>
      <div className="grid grid-cols-2 gap-3">
        <Stat Icon={Package} value={summary?.total_bookings ?? 0} label={t("Pickups booked till date")} testid="profile-total-booked" />
        <Stat Icon={Clock} value={summary?.pending ?? 0} label={t("Pending pickups")} testid="profile-pending" />
        <Stat Icon={CheckCircle2} value={c.Completed ?? 0} label={t("Completed pickups")} testid="profile-total-pickups" />
        <Stat Icon={XCircle} value={c.Cancelled ?? 0} label={t("Cancelled pickups")} testid="profile-cancelled" />
        <Stat Icon={IndianRupee} value={`₹${summary?.total_earned ?? 0}`} label={t("Total money earned")} testid="profile-total-earned" />
        <Stat Icon={Clock} value={`₹${summary?.payment_pending ?? 0}`} label={t("Payment pending")} testid="profile-payment-pending" />
        <Stat Icon={Leaf} value={`${summary?.impact?.total_kg ?? 0} kg`} label={t("Total recycled")} testid="profile-total-kg" />
        <Stat Icon={MessageSquare} value={`${summary?.complaints_open ?? 0} / ${summary?.complaints_total ?? 0}`} label={t("Open / total complaints")} testid="profile-complaints" />
        <Stat Icon={Trophy} value={`#${summary?.rank ?? "–"}`} label={t("Leaderboard rank")} testid="profile-rank" />
        <Stat Icon={CalendarDays} value={since} label={t("Member since")} testid="profile-member-since" />
      </div>
      <PointsCard />
      <ReferralCard />
      <InviteLeaderboard />
      <Card className="space-y-3" data-testid="profile-checks">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[#0D3B2B]"><ShieldCheck className="h-5 w-5 text-[#2E9E6B]" />{t("Account verification")}</h2>
          <span className="text-sm font-bold text-[#2E9E6B]" data-testid="profile-checks-count">{done}/{checks.length}</span>
        </div>
        <div className="h-2 rounded-full bg-[#EAEFEA]"><div className="h-2 rounded-full bg-[#2E9E6B]" style={{ width: `${checks.length ? (done / checks.length) * 100 : 0}%` }} /></div>
        {checks.map((x) => (
          <p key={x.label} className={`flex items-center gap-2 text-sm ${x.done ? "text-[#0D3B2B]" : "text-[#7B968B]"}`}>
            {x.done ? <CheckCircle2 className="h-4 w-4 text-[#2E9E6B]" /> : <Circle className="h-4 w-4" />}{t(x.label)}
          </p>
        ))}
        <p className="text-xs text-[#7B968B]">{t("Verified accounts get pickups confirmed faster. Add missing details in Edit profile or Settings.")}</p>
      </Card>
      <div className="divide-y divide-[#EAEFEA] overflow-hidden rounded-3xl border border-[#D2E0D8] bg-white">
        {links.map(([to, Icon, label, tid]) => (
          <Link key={to} to={to} data-testid={tid} className="flex h-16 items-center gap-3 px-4 font-semibold text-[#0D3B2B] active:bg-[#F4F8F5]">
            <Icon className="h-5 w-5 text-[#2E9E6B]" /><span className="flex-1">{t(label)}</span><ChevronRight className="h-5 w-5 text-[#7B968B]" />
          </Link>
        ))}
        <button onClick={logout} data-testid="profile-logout-btn" className="flex h-16 w-full items-center gap-3 px-4 font-semibold text-[#E05C3A]">
          <LogOut className="h-5 w-5" /> {t("Log out")}
        </button>
      </div>
    </div>
  );
}
